// Adds locale-aware formatter functions to a compiled chart option. Only trusted code adds
// functions: the compiled option itself stays plain data.
import type { CompiledChart } from './charts.ts'
import { formatValue } from './format.ts'

export const escapeHtml = (text: unknown) => String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))

// Not structuredClone: the option can hold arrays from a Vue-reactive document, and proxies cannot be cloned.
const clonePlain = (value: unknown): any => {
  if (Array.isArray(value)) return value.map(clonePlain)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clonePlain(item)]))
  }
  return value
}

export function withFormatters (chart: CompiledChart, locale: string): Record<string, unknown> {
  const fmt = (value: unknown) => typeof value === 'number'
    ? formatValue(value, { ...chart.valueFormat, locale })
    : escapeHtml(value)
  const compact = (value: unknown) => typeof value === 'number'
    ? formatValue(value, { ...chart.valueFormat, locale, compact: Math.abs(value) >= 10000 })
    : String(value)
  const option: Record<string, any> = clonePlain(chart.option)
  const row = (color: unknown, label: unknown, value: string) =>
    `<div style="display:flex;gap:12px;justify-content:space-between;align-items:center">` +
    `<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:8px;height:8px;border-radius:50%;background:${escapeHtml(typeof color === 'string' ? color : '#999')}"></span>${escapeHtml(label)}</span>` +
    `<strong style="font-variant-numeric:tabular-nums">${value}</strong></div>`
  if (option.tooltip) {
    option.tooltip.formatter = (params: any) => {
      const list = Array.isArray(params) ? params : [params]
      const head = list[0]?.axisValueLabel ?? list[0]?.name ?? ''
      const rows = list.map((p: any) => {
        if (p.seriesType === 'radar' && Array.isArray(p.value)) {
          const indicators: any[] = option.radar?.indicator ?? []
          return `<div style="margin-bottom:4px;font-weight:600">${escapeHtml(p.name)}</div>` +
            indicators.map((ind, i) => row(p.color, ind.name, fmt(p.value[i]))).join('')
        }
        if (p.seriesType === 'candlestick' && Array.isArray(p.value)) {
          const [, open, close, low, high] = p.value
          return row(p.color, p.seriesName, `${fmt(open)} → ${fmt(close)}`) +
            `<div style="opacity:.7;text-align:end">${fmt(low)} – ${fmt(high)}</div>`
        }
        if (p.dataType === 'edge') {
          const label = `${p.data?.source ?? ''} → ${p.data?.target ?? ''}`
          return row(p.color, label, typeof p.value === 'number' ? fmt(p.value) : escapeHtml(p.value))
        }
        const raw = Array.isArray(p.value) ? p.value[p.value.length - 1] : p.value
        const share = typeof p.percent === 'number' ? ` · ${p.percent.toFixed(1)} %` : ''
        const label = Array.isArray(params) ? p.seriesName : (p.name || p.seriesName)
        return row(p.color, label, `${fmt(raw)}${share}`)
      }).join('')
      return (head && Array.isArray(params) ? `<div style="margin-bottom:6px;opacity:.7">${escapeHtml(head)}</div>` : '') + rows
    }
  }
  for (const series of [option.series].flat().filter(Boolean)) {
    if (series.type === 'gauge' && series.detail) {
      series.detail.formatter = (value: number) => String(fmt(value))
    }
  }
  if (option.visualMap) {
    option.visualMap.formatter = (value: number) => String(compact(value))
  }
  const axes = [option.xAxis, option.yAxis].flat().filter(Boolean)
  for (const axis of axes) {
    if (axis.type === 'value' && axis.axisLabel && axis.axisLabel.show !== false) {
      axis.axisLabel.formatter = compact
    }
  }
  return option
}
